import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Rocket,
  ArrowRight,
  Target,
  Users,
  Calendar,
  FileText,
  Bot,
  BarChart,
  Shield,
  Clock,
  Settings,
  ChevronRight
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { animations } from '@/lib/animations';
import { ROUTES, CALENDLY_URL } from '@/lib/constants';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/lib/translations';

const StartPOC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language].poc;

  const steps = [
    {
      icon: <Target className="w-6 h-6 text-primary" />,
      title: t.process.steps.objectives.title,
      description: t.process.steps.objectives.description
    },
    {
      icon: <BarChart className="w-6 h-6 text-primary" />,
      title: t.process.steps.analysis.title,
      description: t.process.steps.analysis.description
    },
    {
      icon: <Bot className="w-6 h-6 text-primary" />,
      title: t.process.steps.setup.title,
      description: t.process.steps.setup.description
    },
    {
      icon: <Rocket className="w-6 h-6 text-primary" />,
      title: t.process.steps.pilot.title,
      description: t.process.steps.pilot.description
    }
  ];

  const benefits = [
    {
      icon: <Shield className="w-6 h-6 text-primary" />,
      title: t.benefits.items.risk.title,
      description: t.benefits.items.risk.description
    },
    {
      icon: <Clock className="w-6 h-6 text-primary" />,
      title: t.benefits.items.results.title,
      description: t.benefits.items.results.description
    },
    {
      icon: <Settings className="w-6 h-6 text-primary" />,
      title: t.benefits.items.customization.title,
      description: t.benefits.items.customization.description
    },
    {
      icon: <Users className="w-6 h-6 text-primary" />,
      title: t.benefits.items.support.title,
      description: t.benefits.items.support.description
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="flex flex-col items-center justify-center min-h-[70vh] pb-8 pt-6 md:pt-10 md:pb-12 lg:pt-16 lg:pb-24 relative overflow-hidden w-full">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
        <div className="relative mx-auto flex max-w-[980px] flex-col items-center gap-8 text-center px-4">
          <div className={`inline-block p-3 rounded-2xl bg-primary/10 mb-6 ${animations.scaleUp}`}>
            <Rocket className="w-12 h-12 text-primary" />
          </div>
          <h1 className={`text-4xl font-bold leading-tight tracking-tighter md:text-7xl lg:leading-[1.1] bg-gradient-to-r from-primary via-primary/80 to-primary/50 bg-clip-text text-transparent ${animations.fadeInSlideUp}`}>
            {t.hero.title}
          </h1>
          <p className={`max-w-[750px] text-xl text-muted-foreground sm:text-2xl ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {t.hero.subtitle}
          </p>
          <div className={`flex flex-col gap-4 sm:flex-row sm:gap-6 ${animations.fadeInSlideUp} ${animations.delay300}`}>
            <Button 
              size="lg" 
              className="group text-lg px-8 py-6"
              onClick={() => navigate(ROUTES.CONTACT_SALES)}
            >
              {t.hero.scheduleConsultation}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="text-lg px-8 py-6"
              onClick={() => {
                const processSection = document.getElementById('process-section');
                if (processSection) {
                  processSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              {t.hero.viewProcess}
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process-section" className={`w-full py-16 bg-muted/30 ${animations.fadeIn}`}>
        <div className="mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className={`text-3xl font-bold leading-tight tracking-tighter md:text-5xl text-center mb-12 ${animations.fadeInSlideUp}`}>
              {t.process.title}
            </h2>
            <div className="relative">
              {/* Vertical Line */}
              <div className="absolute left-8 top-0 bottom-0 w-px bg-primary/20" />
              
              {/* Timeline Steps */}
              <div className="space-y-12">
                {steps.map((step, index) => (
                  <div 
                    key={index}
                    className={`relative flex gap-8 ${animations.fadeInSlideUp}`}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {/* Icon Circle */}
                    <div className="relative">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center z-10 relative">
                        {step.icon}
                      </div>
                      {index < steps.length - 1 && (
                        <div className="absolute top-16 left-1/2 bottom-0 w-px bg-primary/20" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 pt-3">
                      <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                      <p className="text-muted-foreground">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={`w-full py-16 ${animations.fadeIn}`}>
        <div className="mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className={`text-3xl font-bold leading-tight tracking-tighter md:text-5xl text-center mb-12 ${animations.fadeInSlideUp}`}>
              {t.benefits.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <Card 
                  key={index}
                  className={`p-6 border-primary/10 hover:shadow-lg transition-all ${animations.fadeInSlideUp}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex flex-col gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg w-fit">
                      {benefit.icon}
                    </div>
                    <h3 className="text-xl font-bold">{benefit.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`w-full py-16 ${animations.fadeIn}`}>
        <div className="mx-auto px-4">
          <Card className="max-w-4xl mx-auto p-8 bg-primary/5">
            <div className="text-center">
              <h2 className={`text-3xl font-bold leading-tight tracking-tighter md:text-5xl mb-4 ${animations.fadeInSlideUp}`}>
                {t.cta.title}
              </h2>
              <p className={`text-xl text-muted-foreground mb-8 ${animations.fadeInSlideUp} ${animations.delay200}`}>
                {t.cta.description}
              </p>
              <div className={`flex flex-col sm:flex-row gap-4 justify-center ${animations.fadeInSlideUp} ${animations.delay300}`}>
                <Button 
                  size="lg" 
                  className="group text-lg px-8 py-6"
                  onClick={() => navigate(ROUTES.CONTACT_SALES)}
                >
                  {t.cta.schedule}
                  <Calendar className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="text-lg px-8 py-6"
                  onClick={() => navigate('/resources')}
                >
                  {t.cta.resources}
                  <FileText className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default StartPOC; 